import type _jquery from "jquery";
import { exhaustiveGuard, isNotNil } from "@mcwd/typescript-type-guards";
import type { GatingState } from "../../../types/definitions/gating/gating-state.js";
import { GatingLevel, type GatingLevelFor } from "../../../types/definitions/gating/gating-level.js";
import { FormOrFormExperienceType } from "../FormOrFormExperienceType.js";

function getHighestPriorityFormGatingLevel<TGatingState extends GatingState.FormExperience>(
  gatingStates: readonly TGatingState[]
): GatingLevel.MarketoForm | null 
{
  if (gatingStates.length === 0) {
    return null;
  }
  if (gatingStates.includes("FullGate" as TGatingState)) {
    return "FullGate";
  }
  if (gatingStates.includes("PartialGate" as TGatingState)) {
    return "PartialGate";
  }
  if (gatingStates.includes("NoGate" as TGatingState) || gatingStates.includes("BlindSubmit" as TGatingState)) {
    return null;
  }
  const gatingStatesStr = gatingStates.map((s: string) => `"${s}"`).join(", ");
  throw new Error(`Unexpected GatingState.FormExperience types in [${gatingStatesStr}]`);
}

function getHighestPriorityExperienceGatingLevel<TGatingState extends GatingState.FormExperience>(
  gatingStates: readonly TGatingState[]
): GatingLevel.FormExperience | null
{
  if (gatingStates.length === 0) {
    return null;
  }
  const highestGatingLevel = getHighestPriorityFormGatingLevel(gatingStates);
  if (isNotNil(highestGatingLevel)) {
    return highestGatingLevel;
  }
  if (gatingStates.includes("NoGate" as TGatingState)) {
    return "NoGate";
  }
  const gatingStatesStr = gatingStates.map((s: string) => `"${s}"`).join(", ");
  throw new Error(`Unexpected GatingState.FormExperience types in [${gatingStatesStr}]`);
}

export function getHighestPriorityGatingLevel<
  TFormOrExperience extends FormOrFormExperienceType,
  TGatingState extends GatingState.FormExperience
> (
  type: TFormOrExperience,
  gatingStates: readonly TGatingState[]
): GatingLevelFor<TFormOrExperience> | null
{
  type ThisReturnType = GatingLevelFor<TFormOrExperience> | null;

  switch (type) {
    case "MarketoForm":
      return getHighestPriorityFormGatingLevel(gatingStates) as ThisReturnType;
    case "FormExperience":
      return getHighestPriorityExperienceGatingLevel(gatingStates) as ThisReturnType;
    default:
      exhaustiveGuard(type);
  }
}